import { faCircleNotch, faTrashCan, faWarning } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import React, { useEffect, useState } from "react";
import { Dropdown } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
    getAppNamesFromUsageData,
    getPlaylistNamesFromUsageData,
} from "userful-chronos-app-common-js/dist/models/sam/Common";
import { SamSourceDef } from "userful-chronos-app-common-js/dist/models/sam/SAMSource";
import {
    sourceSliceActions,
    useSourceAssetDispatch,
    useSourceAssetSelector,
} from "userful-chronos-common-store/dist/sourceassets";
import Button from "../../button/Button";
import { UserfulConfirmationDialog } from "../../widgets";
import UserfulTooltip from "../../Widgets/UserfulTooltip";
import "./Source.scss";

interface IProps {
    source: SamSourceDef;
    onCard?: boolean;
    open?: boolean;
    onOpenChange?: (open: boolean) => void;
    renderTrigger?: (open: () => void) => React.ReactNode;
    hideTrigger?: boolean;
}

export default function SourceDeletionDialog(props: IProps) {
    const { t } = useTranslation();
    const pendingDeleteSource = useSourceAssetSelector((state) => state.sourceStore.pendingDeleteSource);

    const dispatch = useSourceAssetDispatch();
    const isControlled = props.open !== undefined;
    const [showInternal, setShowInternal] = useState(false);
    const show = isControlled ? !!props.open : showInternal;

    const setShowState = (next: boolean) => {
        if (isControlled) {
            props.onOpenChange?.(next);
        } else {
            setShowInternal(next);
        }
    };

    const handleClose = () => {
        dispatch(sourceSliceActions.clearPendingUpdateOrDelete());
        setShowState(false);
    };

    const handleShow = () => {
        setShowState(true);
    };

    useEffect(() => {
        if (pendingDeleteSource && pendingDeleteSource.receivedResponse && pendingDeleteSource.usageData.length < 1) {
            handleClose();
        }
    }, [pendingDeleteSource]);

    const handleDelete = () => {
        dispatch(sourceSliceActions.deleteSourceToServer(props.source.id.value));
    };

    const handleForceDelete = () => {
        dispatch(sourceSliceActions.forceDeleteSourceToServer(props.source.id.value));
        setShowState(false);
    };

    const isDeleting = pendingDeleteSource && !pendingDeleteSource.receivedResponse;
    const showForceDeleteConfirm =
        pendingDeleteSource && pendingDeleteSource.receivedResponse && pendingDeleteSource.usageData.length > 0;

    const renderTrigger = () => {
        if (props.hideTrigger) return null;
        if (props.renderTrigger) return props.renderTrigger(handleShow);

        if (props.onCard) {
            return (
                <UserfulTooltip placement="top" title={t("Shared.delete")}>
                    <div className="cardButton delete-source-button" onClick={handleShow}>
                        <FontAwesomeIcon icon={faTrashCan} />
                    </div>
                </UserfulTooltip>
            );
        }

        return (
            <Dropdown.Item
                as="li"
                eventKey="2"
                id={`${props.source.id.value}-source-delete`}
                onClick={(e) => {
                    e.stopPropagation();
                    handleShow();
                }}
            >
                <div>{t("Shared.delete")}</div>
            </Dropdown.Item>
        );
    };

    return (
        <>
            {renderTrigger()}

            <UserfulConfirmationDialog
                show={show}
                onCancel={handleClose}
                icon={{ icon: faWarning, color: "#D13E2C" }}
                title={t("CommonUI.Sources.deleteTitle")}
                replaceBody={
                    !showForceDeleteConfirm ? (
                        <p>
                            {t("CommonUI.Sources.deleteConfirm")}{" "}
                            <strong style={{ color: "#D13E2C" }}>{`${props.source.name}`}</strong>?
                        </p>
                    ) : (
                        <div className="forceUpdateSourceBody">
                            <p>
                                <strong style={{ color: "#D13E2C" }}>{`${props.source.name}`}</strong>{" "}
                                {t("CommonUI.forceConfirm")}:
                            </p>
                            <ul>
                                {getAppNamesFromUsageData(pendingDeleteSource).map((item) => (
                                    <li key={`app-name-${item}`}>{t(item)}</li>
                                ))}
                                {getPlaylistNamesFromUsageData(pendingDeleteSource).map((item) => (
                                    <li key={`playlist-name-${item}`}>{`${t("Shared.playlist")} "${item}"`}</li>
                                ))}
                            </ul>
                            <p>{t("CommonUI.forceDeleteConfirm")}</p>
                        </div>
                    )
                }
                replaceFooter={
                    <>
                        <Button variant="secondary" onClick={handleClose}>
                            {t("Shared.cancel")}
                        </Button>
                        <Button
                            variant="danger"
                            disabled={isDeleting}
                            onClick={showForceDeleteConfirm ? handleForceDelete : handleDelete}
                        >
                            {t("Shared.delete")} {isDeleting && <FontAwesomeIcon icon={faCircleNotch} spin />}
                        </Button>
                    </>
                }
            />
        </>
    );
}
